/**
 * LE JOURNAL PUBLIÉ (§8.1, design §3) — un document, pas une page d'atterrissage.
 *
 * Ni en-tête de jeu, ni bouton de connexion, ni invitation à s'inscrire : c'est `App` qui l'écarte
 * de la reliure, et c'est délibéré. Quelqu'un ouvre ce lien parce qu'un ami lui a dit « regarde ce
 * qui m'est arrivé » — pas pour découvrir un produit. Une page qui essaierait de le convertir
 * trahirait la personne qui a partagé.
 *
 * CE QU'IL MONTRE : le héros, l'aventure, la fin et sa cause, la carte telle qu'elle a été
 * découverte, et le journal. C'est ce que le serveur consent à rendre, et rien de plus n'est
 * demandé ici : **un journal partagé raconte un parcours, il ne prête pas l'aventure.** Le texte
 * des nœuds, les choix qui n'ont pas été pris, les caches et leur contenu ne franchissent pas
 * l'API — le front n'a donc rien à filtrer, seulement à bien montrer.
 */
import { useRessource } from "../api/chargement.js";
import type { CarteEnVue, JournalPublie as JournalEnVue } from "../api/types.js";
import { CarteDuMonde } from "../composants/CarteDuMonde.js";
import { Logo } from "../composants/Logo.js";
import { Sceau } from "../composants/Sceau.js";
import { Filet, Parchemin } from "../composants/Parchemin.js";
import { ARCHETYPES, dateDite, dureeDite } from "../lexique/mots.js";
import { ditLaLigne } from "../lexique/journal.js";
import { t, tn } from "../lexique/langue.js";

export function JournalPublie({ jeton }: { jeton: string }) {
  const { donnees, erreur, enCours } = useRessource<JournalEnVue>(
    `/public/journaux/${encodeURIComponent(jeton)}`,
    (charge: JournalEnVue) => charge
  );

  if (enCours) return <div className="page" aria-busy="true" />;

  // UN LIEN RÉVOQUÉ DIT QU'IL EST RÉVOQUÉ, et ne renvoie pas vers l'accueil. La personne qui l'ouvre
  // n'a rien fait de mal : elle a cliqué sur ce qu'on lui a donné.
  if (erreur || !donnees) {
    return (
      <div className="page page--etroite arrivee">
        <h1>{t("Ce carnet n'est plus ouvert")}</h1>
        <p className="douce">
          {t("Le lien a été retiré par celui ou celle qui l'avait partagé, ou il n'a jamais existé.")}
        </p>
      </div>
    );
  }

  const carnet = donnees;
  const mort = carnet.issue === "morte";
  // La carte réutilise le composant du jeu : c'est la même carte, avec le même brouillard. Ce qui
  // n'a pas été vu n'est pas dans la réponse — publier son journal ne publie pas l'aventure.
  // Pas de boussole sur un journal publié : l'outil répond « de quel côté est votre objectif », et
  // un lecteur n'a pas d'objectif — il lit ce qui a eu lieu.
  const carte: CarteEnVue = { ici: null, lieu: null, hexagones: carnet.carte, boussole: null };

  return (
    <div className="page page--etroite arrivee carnet">
      <header className="entete-decran entete-decran--centre">
        <Sceau empreinte={mort ? "mort" : "fin"} taille={72} titre={mort ? t("Sceau du cimetière") : t("Sceau de la fin")} />
        <h1>{carnet.heros.nom}</h1>
        <p className="douce">
          {ARCHETYPES[carnet.heros.archetype as keyof typeof ARCHETYPES]?.nom ?? carnet.heros.archetype} —{" "}
          {carnet.aventure}
        </p>
        <p className="epitaphe__ligne">
          {mort ? (
            <>
              {carnet.cause
                ? t("Tombé après {duree} de voyage, {cause}.", {
                    duree: dureeDite(carnet.heures),
                    cause: carnet.cause
                  })
                : t("Tombé après {duree} de voyage.", { duree: dureeDite(carnet.heures) })}
            </>
          ) : (
            <>{t("Revenu au bout de {duree} de voyage.", { duree: dureeDite(carnet.heures) })}</>
          )}
          {carnet.cicatrices > 0 ? (
            <>
              {" "}
              {tn(carnet.cicatrices, "{n} cicatrice en chemin.", "{n} cicatrices en chemin.")}
            </>
          ) : null}
        </p>
        <p className="faible">
          {carnet.finiLe
            ? t("Parti le {depart}, arrivé au bout le {arrivee}.", {
                depart: dateDite(carnet.commenceLe),
                arrivee: dateDite(carnet.finiLe)
              })
            : t("Parti le {depart}.", { depart: dateDite(carnet.commenceLe) })}
        </p>
      </header>

      {carnet.carte.length ? (
        <>
          <Filet>{t("Ce qu'il a vu du monde")}</Filet>
          <div style={{ marginTop: "var(--e4)" }}>
            <CarteDuMonde carte={carte} hauteur={420} />
          </div>
        </>
      ) : null}

      <div style={{ marginTop: "var(--e7)" }}>
        <Filet>{t("Le carnet de bord")}</Filet>
      </div>
      <Parchemin variante="creux">
        <ol className="journal">
          {carnet.journal.map((ligne, index) => (
            <li key={index}>
              <span className="journal__heure nombre">{dureeDite(ligne.heure)}</span>
              <span>{ditLaLigne(ligne)}</span>
            </li>
          ))}
          {!carnet.journal.length ? <li className="faible">{t("Rien n'a été écrit.")}</li> : null}
        </ol>
      </Parchemin>

      {/* LA SEULE MENTION DU JEU, EN BAS, ET SANS APPEL À L'ACTION. On dit d'où vient le document ;
          on ne demande rien. Une page qui essaierait de convertir le lecteur trahirait la personne
          qui lui a envoyé ce lien. */}
      <footer className="carnet__origine">
        <Logo hauteur={26} />
        <span className="faible">{t("Un carnet de Destinia — le livre dont on ne sort qu'une fois.")}</span>
      </footer>
    </div>
  );
}
